import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage, useIntl} from 'react-intl';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CircularProgress from '@mui/material/CircularProgress';
import FileUploadButton from './FileUploadButton';
import {DOC_STATUSES} from '../constants/documentCollection';

const useStyles = makeStyles()((theme) => ({
  singleUploadCard: {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 12,
    padding: 16,
    textAlign: 'center',
    marginTop: 12,
  },
  singleUploadCardInReview: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.lighter,
  },
  singleUploadCardApproved: {
    borderColor: theme.palette.success.main,
    backgroundColor: theme.palette.success.light,
  },
  singleUploadCardRejected: {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.light,
  },
  singleUploadTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    marginBottom: 4,
  },
  singleUploadDesc: {
    fontSize: theme.typography.pxToRem(13),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.secondary,
    marginBottom: 12,
  },
  singleUploadRange: {
    fontSize: theme.typography.pxToRem(13),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.secondary,
    marginBottom: 12,
  },
  singleFileRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 6,
  },
  singleFileName: {
    color: theme.palette.success.main,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
  },
  singleUploadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginTop: 12,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
  },
  singleStatusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    fontFamily: 'Ploni, Arial, sans-serif',
    marginTop: 12,
    backgroundColor: `${theme.palette.primary.main}1a`,
    color: theme.palette.primary.main,
  },
  singleStatusBadgeApproved: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    fontFamily: 'Ploni, Arial, sans-serif',
    marginTop: 12,
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.main,
  },
  singleStatusBadgeRejected: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    fontFamily: 'Ploni, Arial, sans-serif',
    marginTop: 12,
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.error.main,
  },
}));

const SingleInsuranceHistoryCard = ({documentCount, yearsRange, status, files, uploading, onFileSelected}) => {
  const {classes} = useStyles();
  const intl = useIntl();

  const cardStatusClass = status === DOC_STATUSES.NOT_APPROVED ? classes.singleUploadCardRejected
    : status === DOC_STATUSES.APPROVED ? classes.singleUploadCardApproved
    : status === DOC_STATUSES.IN_REVIEW ? classes.singleUploadCardInReview
    : '';

  return (
    <div className={`${classes.singleUploadCard} ${cardStatusClass}`}>
      <div className={classes.singleUploadTitle}>
        <FormattedMessage id='docCollection.upload.pathB.singleTitle' values={{count: documentCount}} />
      </div>
      <div className={classes.singleUploadRange}>{yearsRange}</div>
      <div className={classes.singleUploadDesc}>
        <FormattedMessage id='docCollection.upload.pathB.singleDesc' />
      </div>
      {files.length > 0 && (
        <div>
          {files.map((fileData, index) => (
            <div className={classes.singleFileRow} key={index}>
              <span className={classes.singleFileName}>
                ✓ {fileData.file.name}
              </span>
            </div>
          ))}
        </div>
      )}
      {uploading && (
        <div className={classes.singleUploadingRow}>
          <CircularProgress size={20} />
          <span>{intl.formatMessage({id: 'docCollection.upload.uploading'})}</span>
        </div>
      )}
      {!uploading && status === DOC_STATUSES.IN_REVIEW && (
        <div style={{textAlign: 'center'}}>
          <span className={classes.singleStatusBadge}>
            <ScheduleIcon style={{fontSize: 16}} />
            {intl.formatMessage({id: 'docCollection.status.inReview'})}
          </span>
        </div>
      )}
      {!uploading && status === DOC_STATUSES.APPROVED && (
        <div style={{textAlign: 'center'}}>
          <span className={classes.singleStatusBadgeApproved}>
            <CheckCircleOutlineIcon style={{fontSize: 16}} />
            {intl.formatMessage({id: 'docCollection.status.approved'})}
          </span>
        </div>
      )}
      {!uploading && status === DOC_STATUSES.NOT_APPROVED && (
        <div style={{textAlign: 'center'}}>
          <span className={classes.singleStatusBadgeRejected}>
            {intl.formatMessage({id: 'docCollection.status.notApproved'})}
          </span>
        </div>
      )}
      {!uploading && status === DOC_STATUSES.REQUIRED && (
        <FileUploadButton onFileSelected={onFileSelected} />
      )}
      {!uploading && status === DOC_STATUSES.NOT_APPROVED && (
        <div style={{marginTop: 12}}>
          <FileUploadButton
            onFileSelected={onFileSelected}
            label={intl.formatMessage({id: 'docCollection.fileUpload.reuploadLabel'})}
          />
        </div>
      )}
    </div>
  );
};

SingleInsuranceHistoryCard.propTypes = {
  documentCount: PropTypes.number.isRequired,
  files: PropTypes.arrayOf(PropTypes.shape({file: PropTypes.object})),
  onFileSelected: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  uploading: PropTypes.bool,
  yearsRange: PropTypes.string,
};

export default SingleInsuranceHistoryCard;
